import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { IconToken } from '../icons';

/**
 * The Token meter (docs/TOKENS_PLAN.md §7).
 *
 * It lives ON THE COMPOSER rather than in the toolbar, because that is where the decision is made.
 * The toolbar deliberately shed its gauges — they read as dashboard furniture next to the work —
 * and a number a learner glances at once on load teaches nothing. Next to the send button it
 * answers the only question that matters at the only moment it matters: what is this prompt about
 * to cost, and can I afford it?
 *
 * Tokens, never provider tokens or dollars. The meter exists to teach prompt economy; a teenager
 * cannot reason about either of the other two, and a price in dollars invites them to put a number
 * on their own curiosity.
 */

export interface TokenState {
  balance: number;
  enforced: boolean;
  estimates: { generate: number; edit: number; runtime_text: number; image: number };
}

/** Shared loader so the composer and the account chip agree without two round trips. */
export async function fetchTokens(): Promise<TokenState | null> {
  try {
    return await api.get<TokenState>('/box/me/tokens');
  } catch {
    return null;
  }
}

export function TokenMeter({
  tokens,
  /** what the pending action is expected to cost — `null` while the action is free */
  estimate,
  className = '',
}: {
  tokens: TokenState | null;
  estimate: number | null;
  className?: string;
}) {
  if (!tokens) return null;

  const low = tokens.enforced && tokens.balance <= (estimate ?? tokens.estimates.edit);
  const empty = tokens.enforced && tokens.balance <= 0;

  return (
    <span
      /* `whitespace-nowrap`: the composer row is narrow and shares it with the mode pills, so
         without this the cost hint wraps mid-phrase and reads as three unrelated fragments. */
      className={['inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] tabular-nums', className].join(' ')}
      title={
        empty
          ? 'Out of Tokens. Planning, fixing and testing are always free.'
          : `A build is about ${tokens.estimates.generate} Tokens, a change about ${tokens.estimates.edit}, ` +
            `an AI answer inside your app about ${tokens.estimates.runtime_text}. ` +
            'Planning, fixing and testing are free.'
      }
    >
      <span className={empty ? 'text-muted' : low ? 'text-sun' : 'text-accent'}>
        <IconToken width={13} height={13} />
      </span>
      <span className={empty ? 'text-muted' : 'font-semibold text-ink'}>
        {tokens.balance.toLocaleString()}
      </span>
      {estimate !== null && !empty && <span className="text-muted">· ~{estimate} to run</span>}
      {estimate === null && <span className="text-muted">· free</span>}
    </span>
  );
}

/**
 * The header indicator.
 *
 * Takes its balance from the page when the page has one, so the number in the header and the
 * number on the composer can never disagree — two independent fetches of the same fact is how a
 * header ends up a build behind. Falls back to its own fetch on screens that track no balance.
 */
export function TokenBalance({ tokens: given }: { tokens?: TokenState | null } = {}) {
  const [own, setOwn] = useState<TokenState | null>(null);
  useEffect(() => {
    if (given === undefined) void fetchTokens().then(setOwn);
  }, [given]);

  const tokens = given ?? own;
  if (!tokens) return null;

  const empty = tokens.enforced && tokens.balance <= 0;
  const low = tokens.enforced && !empty && tokens.balance <= tokens.estimates.generate;
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-bold tabular-nums',
        empty ? 'bg-page text-muted' : low ? 'bg-sun/20 text-accent-deep' : 'bg-page text-ink',
      ].join(' ')}
      title={
        empty
          ? 'Out of Tokens. Planning, fixing and testing are always free.'
          : `${tokens.balance.toLocaleString()} Tokens — a build is about ${tokens.estimates.generate}, ` +
            `a change about ${tokens.estimates.edit}. Planning, fixing and testing are free.`
      }
    >
      <span className={empty ? 'text-muted' : 'text-accent'}>
        <IconToken width={14} height={14} />
      </span>
      {tokens.balance.toLocaleString()}
    </span>
  );
}
